import { useSessionAnalytics } from "@/lib/useSessionAnalytics"
import { Fragment, useCallback, useEffect, useRef, useState } from "react"
import { Link, useLocation, useNavigate, useParams } from "react-router-dom"
import { getThread, listDocuments } from "../api"
import { isGuestThread, requireLogin, useAuthGate } from "@/lib/authGate"
import type { ThreadRow } from "../contract"

// A guest (one free search while signed out) has no history, matters or uploads.
/** The latest State/side PATCH, so a re-run waits for the server to hold the new choice. */
let pendingPatch: Promise<unknown> = Promise.resolve()
const NO_THREADS = async (): Promise<ThreadRow[]> => []
const GUEST_UPLOAD = async () => {
  requireLogin("upload", "/ask")
  return []
}
import type { TurnMode } from "../contract"
import { useDocsStore } from "../docs.store"
import { useTurnStore } from "../store"
import { NiraBar } from "../ds/NiraBar"
import { patchThread } from "../api"
import { leanDegrees, useBeamStore } from "../beam"
import { changedContext, useReaderContext, type ThreadContext } from "../readerContext"
import { abortAll, canRerun, hydrateThread, rerunLatest, sendTurn } from "./actions"
import { answerText } from "./copyText"
import { AuthoritiesPrint } from "./AuthoritiesPrint"
import { track } from "@/lib/analytics"
import { Dock } from "./Dock"
import { ContextMenu } from "../ds/ContextMenu"
import { PhoneContextChip } from "./ReadingPlanBlock"
import { RUPEES_PROMPT } from "./compose"
import { firstBlock } from "./derive"
import { setInert, useMediaQuery, usePageTitle, useReducedMotion, useStoredTheme } from "./hooks"
import { InThisAnswer } from "./InThisAnswer"
import { ClientCard, ShareDialog } from "./ShareDialog"
import { contextParts } from "./format"
import { Working } from "./Working"
import { SaveToWorkspace, WorkspaceLine, WorkspacePickerHost } from "./WorkspaceEntry"
import { Rail, NEW_QUESTION_TO } from "./Rail"
import { useAnswerSession } from "./session"
import { caseOf } from "./thread"
import { SourcePanel } from "./SourcePanel"
import { useAnswerEngagement } from "./useAnswerEngagement"
import { RouteCard } from "./RouteCard"
import { Turn } from "./Turn"
import { FeedbackRow } from "../../feedback/Feedback"
import type { FeedbackRef } from "../../feedback/store"
import "./answer.css"
import "./answer-v4.css"

const AMOUNT_CHIPS = [{ label: "Add the amounts", text: RUPEES_PROMPT }]

/** Router state the landing page passes when it submits a question. */
interface StartState {
  question?: string
  mode?: TurnMode
  docIds?: string[]
  doc_ids?: string[]
}

// React 18 StrictMode mounts effects twice in development. Teardown is deferred one tick so a
// remount of the same thread keeps its stream instead of aborting and re-posting the turn.
let teardown: { threadId: string; timer: number } | null = null
// The thread the page is showing now; a slow document list for an older thread is dropped.
let currentThread = ""
// Threads whose first question this tab already sent: a Back to a history entry that still holds
// the landing state must hydrate, not ask again.
const sentFirst = new Set<string>()

function startThread(threadId: string, state: StartState | null) {
  currentThread = threadId
  useTurnStore.getState().clear()
  useAnswerSession.getState().reset()
  useDocsStore.setState({ docs: {} })
  const docIds = state?.docIds ?? state?.doc_ids ?? []
  if (docIds.length) {
    listDocuments(threadId)
      .then((rows) => {
        if (currentThread === threadId && Array.isArray(rows)) useDocsStore.getState().setRows(rows)
      })
      .catch(() => undefined)
  }
  if (state?.question) {
    void sendTurn(threadId, { message: state.question, mode: state.mode ?? "ask", doc_ids: docIds })
  } else {
    void hydrateThread(threadId)
  }
}

/** Nira answer page (Question-v4 / Results-v4): top bar, the thread read top to bottom, the "In this answer" rail, sources and the follow-up composer. */
export default function AnswerPage() {
  const { threadId = "" } = useParams<{ threadId: string }>()
  useSessionAnalytics("nira", threadId)
  const location = useLocation()
  const { signedIn } = useAuthGate()
  const navigate = useNavigate()
  useStoredTheme()

  const order = useTurnStore((s) => s.order)
  const turns = useTurnStore((s) => s.turns)
  const meta = useAnswerSession((s) => s.meta)
  const threadTitle = useAnswerSession((s) => s.title)
  const caseFile = useAnswerSession((s) => s.caseFile)
  // A notice read in this thread starts a case file on the server; pick it up once a document
  // is ready (a moment later, since the case is made just after the document settles).
  const readyDocs = useDocsStore((s) => Object.values(s.docs).filter((d) => d.status === "ready").length)
  useEffect(() => {
    if (!readyDocs || caseFile) return
    const t = window.setTimeout(() => {
      getThread(threadId)
        .then((d) => useAnswerSession.getState().setCaseFile(caseOf(d)))
        .catch(() => undefined)
    }, 1500)
    return () => window.clearTimeout(t)
  }, [readyDocs, caseFile, threadId])
  const threadCtx = useReaderContext((s) => s.threads[threadId])
  const preferred = useReaderContext((s) => s.preferred)
  const notice = useAnswerSession((s) => s.notice)
  const route = useAnswerSession((s) => s.route)
  const source = useAnswerSession((s) => s.source)
  const wide = useMediaQuery("(min-width: 1180px)")
  const reduced = useReducedMotion()

  const [histOpen, setHistOpen] = useState(false)
  const [activeTurn, setActiveTurn] = useState<string | null>(null)
  const [activeId, setActiveId] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [sharing, setSharing] = useState<"share" | "client" | "client-options" | null>(null)
  const histRef = useRef<HTMLElement>(null)
  const srcRef = useRef<HTMLElement>(null)
  const threadRef = useRef<HTMLElement>(null)

  // Start or reload the thread.
  useEffect(() => {
    if (teardown && teardown.threadId === threadId) {
      window.clearTimeout(teardown.timer)
      teardown = null
    } else {
      if (teardown) {
        window.clearTimeout(teardown.timer)
        teardown = null
        abortAll()
      }
      const raw = (location.state as StartState | null) ?? null
      const state = raw?.question && sentFirst.has(threadId) ? null : raw
      if (raw?.question) navigate(location.pathname + location.search, { replace: true, state: null })
      if (state?.question) sentFirst.add(threadId)
      startThread(threadId, state)
    }
    return () => {
      const timer = window.setTimeout(() => {
        abortAll()
        currentThread = ""
        teardown = null
      }, 0)
      teardown = { threadId, timer }
    }
    // location.state is read once per thread on purpose: a reload must not re-send the question.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId])

  const closeSource = useCallback(() => {
    const from = useAnswerSession.getState().source?.from
    useAnswerSession.getState().openSource(null)
    if (from && document.contains(from)) from.focus({ preventScroll: true })
  }, [])

  // Opening a source closes the history drawer that shares the screen.
  useEffect(() => {
    if (source) setHistOpen(false)
  }, [source])

  // Escape closes the source panel first, then the history drawer.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return
      if (useAnswerSession.getState().source) return closeSource()
      if (histOpen) setHistOpen(false)
    }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [histOpen, closeSource])

  useEffect(() => {
    setInert(histRef.current, !histOpen)
    setInert(srcRef.current, !source)
  }, [histOpen, source])

  // Scroll-spy (Results-v5): the part whose top has passed 40% of the viewport lights up in the
  // rail, with its turn. Measured on scroll rather than by IntersectionObserver entries, which only
  // report the parts that changed and so lose the current one between two observations.
  useEffect(() => {
    const root = threadRef.current
    if (!root) return
    let frame = 0
    const measure = () => {
      frame = 0
      const parts = [...root.querySelectorAll<HTMLElement>(".averdict, .asec[id]")]
      if (!parts.length) return
      const line = window.innerHeight * 0.4
      const atEnd = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4
      let current = parts[0]
      for (const el of parts) if (el.getBoundingClientRect().top <= line) current = el
      if (atEnd) current = parts[parts.length - 1]
      const turn = current.closest<HTMLElement>(".turn")?.dataset.turn
      if (turn) setActiveTurn(turn)
      setActiveId(current.id)
    }
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(measure)
    }
    measure()
    window.addEventListener("scroll", onScroll, { passive: true })
    window.addEventListener("resize", onScroll)
    return () => {
      window.removeEventListener("scroll", onScroll)
      window.removeEventListener("resize", onScroll)
      window.cancelAnimationFrame(frame)
    }
  }, [order, turns])

  // A new turn scrolls into view and becomes active.
  const lastKey = useRef<string | undefined>(undefined)
  useEffect(() => {
    const key = order[order.length - 1]
    if (key === lastKey.current) return
    lastKey.current = key
    if (!key) return
    setActiveTurn(key)
    document.getElementById(`turn-${key}`)?.scrollIntoView({ behavior: reduced ? "auto" : "smooth", block: "start" })
  }, [order, reduced])

  // The top-bar beam: working during a run, settling to the answer's lean when it lands, grey
  // and still while the reader scrolls, resting at the lean otherwise.
  const runningNow = order.some((k) => turns[k]?.status === "running")
  const doneNow = !runningNow && order.length > 0 && turns[order[order.length - 1]]?.status === "done"
  const leanedTurn = [...order].reverse().map((k) => turns[k]).find((v) => v?.head?.lean)
  const lean = leanDegrees(leanedTurn?.head?.lean?.toward)
  useEffect(() => {
    const beam = useBeamStore.getState()
    beam.setLean(lean)
    if (runningNow) return beam.set("working")
    if (!doneNow) return beam.set("idle")
    beam.set("done")
    const rest = window.setTimeout(() => useBeamStore.getState().set("static"), 1300)
    return () => window.clearTimeout(rest)
  }, [runningNow, doneNow, lean])
  useEffect(() => {
    if (!doneNow) return
    let still = 0
    const onScroll = () => {
      useBeamStore.getState().set("reading")
      window.clearTimeout(still)
      still = window.setTimeout(() => useBeamStore.getState().set("static"), 900)
    }
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => {
      window.removeEventListener("scroll", onScroll)
      window.clearTimeout(still)
    }
  }, [doneNow])
  useEffect(() => () => useBeamStore.getState().set("idle"), [])

  const go = (id: string) => {
    setActiveId(id)
    document.getElementById(id)?.scrollIntoView({ behavior: reduced ? "auto" : "smooth", block: "start" })
  }

  const guest = isGuestThread(threadId, signedIn)
  const engagement = useAnswerEngagement(threadId, signedIn && !guest, activeTurn)
  const onSend = (text: string, docIds: string[]) => {
    if (guest) return requireLogin("second_search", "/ask")
    engagement.followup()
    useAnswerSession.getState().openSection(null)
    void sendTurn(threadId, { message: text, mode: "ask", doc_ids: docIds })
  }

  const views = order.map((k) => turns[k]).filter(Boolean)
  const last = views[views.length - 1]
  // The answer worked out no amount: the rupees prompt is a chip in the composer's row.
  const noAmount = last?.status === "done" && !Object.values(last.sections ?? {}).some((sec) => firstBlock(sec, "exposure")?.total)
  const asks = order.filter((k) => (meta[k]?.kind ?? "ask") === "ask")
  const question = asks.length ? turns[asks[asks.length - 1]]?.question ?? "" : ""
  // The breadcrumb names the matter: the answer's title, else the thread's, else the question.
  const named = [...views].reverse().find((v) => v.head?.title)?.head?.title
  const crumb = named || (threadTitle && threadTitle !== "New research" ? threadTitle : "") || question
  usePageTitle(crumb)
  const ctx: ThreadContext = threadCtx ?? preferred
  const onContext = (next: ThreadContext) => {
    useReaderContext.getState().setThread(threadId, next)
    useReaderContext.getState().prefer(next)
    // Only what changed: a State sent alongside a side change would lock the State as "chosen".
    const patch = changedContext(ctx, next)
    if (!patch) return
    pendingPatch = patchThread(threadId, patch).then(() => undefined, () =>
      useAnswerSession.getState().setNotice("Couldn't change who this is for. Try again."),
    )
  }
  const busy = views.some((v) => v.status === "running")
  const rerunnable = canRerun()
  const onRerun = () => void pendingPatch.then(() => rerunLatest(threadId))
  const doneCount = views.filter((v) => v.status !== "running").length
  const scrim = histOpen || (!wide && !!source)
  const reading = activeTurn && turns[activeTurn]?.head ? turns[activeTurn] : [...views].reverse().find((v) => v.head)

  const copy = async () => {
    if (!reading) return
    try {
      await navigator.clipboard.writeText(answerText(reading))
      track("answer_copied", { format: "text" })
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1800)
    } catch {
      useAnswerSession.getState().setNotice("Couldn't copy. Select the text and copy it instead.")
    }
  }
  // Every finished answer is rated on its own, one rating per turn; the id Nira knows the turn by keeps it apart from the others.
  const feedbackFor = (k: string): FeedbackRef | null => {
    const v = turns[k]
    // A partial answer (head, then an error) is still an answer on screen, so it can be rated too.
    return v && (v.status === "done" || v.status === "error") && v.head && v.turnId && !guest
      ? { app: "nira", sessionId: threadId, target: "answer", turnKey: v.turnId, requestId: meta[k]?.requestId }
      : null
  }
  // Always there: a new question starts a fresh thread (the same place as the drawer's "New question").
  const newQuestion = (
    <button type="button" className="nr-bar-act" onClick={() => navigate(NEW_QUESTION_TO)}>
      <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true"><path d="M10 4.5v11M4.5 10h11" /></svg>
      <span>New question</span>
    </button>
  )
  const actions = (
    <>
      {newQuestion}
      {reading && !busy ? <>
      {threadId ? <SaveToWorkspace threadId={threadId} /> : null}
      <button type="button" className="nr-bar-act" onClick={() => setSharing("client")}>
        <span>Forward to client</span>
      </button>
      <button type="button" className="nr-bar-act" onClick={() => void copy()} aria-live="polite">
        <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="7" y="7" width="9" height="9" rx="1.5" /><path d="M13 7V5a1.5 1.5 0 0 0-1.5-1.5h-6A1.5 1.5 0 0 0 4 5v6a1.5 1.5 0 0 0 1.5 1.5H7" /></svg>
        <span>{copied ? "Copied" : "Copy"}</span>
      </button>
      <button type="button" className="nr-bar-act" onClick={() => { track("answer_exported", { format: "print" }); window.print() }}>
        <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 7V3.5h8V7" /><rect x="3" y="7" width="14" height="7" rx="1.5" /><path d="M6 12h8v4.5H6z" /></svg>
        <span>Print</span>
      </button>
      <button type="button" className="nr-bar-act" data-variant="primary" onClick={() => setSharing("share")}>
        <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M10 3v10" /><path d="M6.5 6.5 10 3l3.5 3.5" /><path d="M4 11v4.5A1.5 1.5 0 0 0 5.5 17h9a1.5 1.5 0 0 0 1.5-1.5V11" /></svg>
        <span>Share</span>
      </button>
      </> : null}
    </>
  )

  // While a turn runs on a wide screen, the working panel takes the rail and the answer lands in the column.
  const runningKey = order.find((k) => turns[k]?.status === "running")
  const runningView = runningKey ? turns[runningKey] : undefined
  const stepT = useAnswerSession((s) => (runningKey ? s.stepT[runningKey] : undefined))

  return (
    <div className={`na2 na4${source ? " srcopen" : ""}${histOpen ? " hopen" : ""}`}>
      <NiraBar
        onMenu={() => setHistOpen((v) => !v)}
        menuOpen={histOpen}
        menuControls="nira-hlist"
        crumbs={caseFile
          ? [{ label: "Workspaces", to: "/workspaces" }, { label: caseFile.title, to: `/workspace/${encodeURIComponent(caseFile.id)}` }, { label: crumb || "Research" }]
          : [{ label: "Research", to: "/ask" }, { label: crumb || "New question" }]}
        actions={actions}
        context={{ value: ctx, onChange: onContext, note: "Applies to your next question in this thread.", onRerun: rerunnable ? onRerun : undefined, rerunBusy: busy }}
      />
      <Rail ref={histRef} threadId={threadId} {...(guest ? { loadThreads: NO_THREADS, showMatters: false } : {})} histOpen={histOpen} onHist={setHistOpen} refreshKey={doneCount} drawerOnly caseId={caseFile?.id} scope="research" />
      <div
        className={`scrim${scrim ? " on" : ""}`}
        aria-hidden="true"
        onClick={() => {
          setHistOpen(false)
          if (source) closeSource()
        }}
      />
      <PhoneContextChip.Provider value={<ContextMenu ctx={ctx} onChange={onContext} note="Applies to your next question in this thread." onRerun={rerunnable ? onRerun : undefined} rerunBusy={busy} />}>
      <div className="a4">
        <div className="a4-col">
          {caseFile ? (
            <Link className="a4-case" to={`/case/${encodeURIComponent(caseFile.id)}`}>
              <span>Workspace</span> {caseFile.title} <span aria-hidden="true">→</span>
            </Link>
          ) : null}
          <main className="thread" ref={threadRef} aria-label="Answers">
            {notice ? <div className="rsum notice" role="status">{notice}</div> : null}
            {route ? (
              <RouteCard
                route={route}
                onWithout={() => {
                  useAnswerSession.getState().setRoute(null)
                  navigate("/ask", { state: { prefill: route.question ?? "" } })
                }}
              />
            ) : null}
            {order.map((k) => {
              const fb = feedbackFor(k)
              return (
                <Fragment key={k}>
                  <Turn
                    turnKey={k}
                    threadId={threadId}
                    number={asks.indexOf(k) + 1}
                    inlineWorking={!wide}
                    onGo={go}
                  />
                  {fb ? <FeedbackRow fb={fb} /> : null}
                </Fragment>
              )
            })}
          </main>
          {reading ? <AuthoritiesPrint view={reading} /> : null}
          <div id="followup" className="a4-follow" hidden={busy && views.length <= 1}>
            <p className="lab">Ask a follow-up on this answer</p>
            <Dock key={threadId} threadId={threadId} {...(guest ? { uploadFiles: GUEST_UPLOAD } : {})} followups={last?.status === "done" ? last.followups : []} chips={noAmount ? AMOUNT_CHIPS : []} busy={busy} onSend={onSend} />
          </div>
          <p className="disc">Research, not legal advice. Check the cited text before you rely on it.</p>
        </div>
        <aside className="a4-rail">
          {wide && runningView ? (
            <Working
              id={`rail-ld-${runningKey}`}
              steps={runningView.steps.map((st) => ({ ...st, t: stepT?.[st.id] ?? 0 }))}
              mascot="searching"
              matter={contextParts(runningView.head?.context).join(" · ")}
              after={<WorkspaceLine threadId={threadId} />}
              progress={`${runningView.steps.length}:${runningView.steps.filter((x) => x.status !== "run").length}:${Object.keys(runningView.sections).length}:${runningView.head ? 1 : 0}`}
            />
          ) : (
            <>
              <InThisAnswer activeTurn={activeTurn} activeId={activeId} onGo={go} readsFor={ctx.side} />
            </>
          )}
        </aside>
      </div>
      </PhoneContextChip.Provider>
      <SourcePanel ref={srcRef} onClose={closeSource} />
      <WorkspacePickerHost threadId={threadId} />
      {sharing === "client" && reading ? (
        <ClientCard threadId={threadId} view={reading} onClose={() => setSharing(null)} onMore={() => setSharing("client-options")} />
      ) : sharing && reading ? (
        <ShareDialog threadId={threadId} view={reading} client={sharing === "client-options"} onClose={() => setSharing(null)} />
      ) : null}
    </div>
  )
}
